import { Box, Heading, Stack, Text } from "@chakra-ui/react";
import * as React from "react";

import { useAlertModal } from "modals/useAlertModal";
import DogIcon from "components/Chakra/Icons/Dog";
import EditIcon from "components/Chakra/Icons/Edit";
import Email2Icon from "components/Chakra/Icons/Email2";
import StatsIcon from "components/Chakra/Icons/Stats";
import TrashIcon from "components/Chakra/Icons/Trash";
import CustomLink from "components/Chakra/CustomLink";
import { useTracking } from "contexts/Tracking";
import { useDetailState } from "scenes/OfferDetail";
import { IMarginProps } from "src/types/types";
import DeleteOfferApiEndpoint from "utils/offer/api/DeleteOffer";
import Button from "components/Chakra/Button";
import useRouter from "hooks/useRouter";
import useApiEndpoint from "hooks/useApiEndpoint";
import useHelperCRM, { EAction } from "hooks/useHelperCRM";
import { usePutOfferStatusChangeMutation } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";

export type TInactiveType = "owner" | "customer";

interface IProps extends IMarginProps {
  type: TInactiveType;
}

const InactiveMessage: React.FC<IProps> = ({ type, ...margins }) => {
  const { trackEvent } = useTracking();
  const { navigateTo } = useRouter();
  const { openModal: openAlertModal } = useAlertModal();
  const { createEventOffer, updateUserOffer } = useHelperCRM();
  const [changeOfferStatusApi] = usePutOfferStatusChangeMutation();
  const { callEndpoint: deleteofferApi } = useApiEndpoint(
    DeleteOfferApiEndpoint,
  );

  const { id, status } = useDetailState();

  const deleteOnSubmit = (offerId: number): void => {
    deleteofferApi({ offerId }).then(() => {
      navigateTo("/moje-inzeraty");
    });
  };

  const activateOnSubmit = async (offerId: number) => {
    try {
      createEventOffer({ action: EAction.PUBLISH });

      const data = await changeOfferStatusApi({
        statusChangeRequest: { offerId, status: "activate" },
      });

      if ("data" in data && data.data.success === true) {
        updateUserOffer({
          offerId: offerId.toString(),
          data: {
            id: offerId.toString(),
            date: new Date().toISOString(),
            offerId: offerId.toString(),
            status: "offer_active",
          },
        });

        location.reload();
      } else {
        navigateTo("/cenik/[offerId]", { offerId: offerId.toString() });
      }
    } catch (err) {
      navigateTo("/cenik/[offerId]", { offerId: offerId.toString() });
    }
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="ud-grey-200"
      borderRadius="6px"
      py="20px"
      {...margins}
      data-test="offerDetail.inactive"
    >
      <Heading
        as="h2"
        mb="20px"
        textAlign="center"
        color="ud-grey-600"
        data-test="offerDetail.inactive.heading"
      >
        {type === "owner"
          ? "Váš inzerát momentálně není zveřejněný"
          : "Inzerát už má svého zájemce a nelze jej kontaktovat"}
      </Heading>
      <Stack
        direction={["column", "row"]}
        justify="center"
        alignItems="center"
        spacing="40px"
      >
        {type === "owner" ? (
          <Stack direction="row" flexWrap={["wrap", "nowrap"]} spacing="10px">
            <CustomLink
              showUnderline
              to="/uprava-inzeratu/kontakt/[status]/[id]/[paidId]"
              routeProps={{ id: id?.toString(), status }}
              onClick={() => {
                trackEvent({
                  "dl.eCat": "inzerce",
                  "dl.eAct": "click_upravit",
                  "dl.eLab": "detail_inzeratu",
                });
              }}
              dataTest="offerDetail.inactive.edit"
            >
              <Stack direction="row" align="center" spacing="4px">
                <EditIcon fillWith="ud-primary" boxSize="13px" />
                <Text size="medium" color="ud-primary">
                  Upravit
                </Text>
              </Stack>
            </CustomLink>
            <CustomLink
              showUnderline
              to="/statistiky-inzeratu/[offerId]"
              routeProps={{ offerId: id?.toString() }}
              onClick={() => {
                trackEvent({
                  "dl.eCat": "inzerce",
                  "dl.eAct": "click_statistiky",
                  "dl.eLab": "detail_inzeratu",
                });
              }}
              dataTest="offerDetail.inactive.stats"
            >
              <Stack direction="row" align="center" spacing="4px">
                <StatsIcon fillWith="ud-primary" boxSize="13px" />
                <Text size="medium" color="ud-primary">
                  Statistiky
                </Text>
              </Stack>
            </CustomLink>
            <Button
              variant="link"
              data-test="offerDetail.inactive.trash"
              onClick={() => {
                trackEvent({
                  "dl.eCat": "inzerce",
                  "dl.eAct": "click_smazat",
                  "dl.eLab": "detail_inzeratu",
                });
                openAlertModal({
                  title: "Opravdu chcete inzerát smazat?",
                  children: (
                    <Text textAlign="center">
                      Stačí, aby inzerát <b>nebyl zveřejněný</b>
                      <br />a nikdo ho neuvidí.
                    </Text>
                  ),
                  cancelText: "NE, ponechat",
                  submitText: "ANO, smazat",
                  onSubmit: () => {
                    if (id) {
                      deleteOnSubmit(id);
                    }
                  },
                });
              }}
            >
              <Stack direction="row" align="center" spacing="4px">
                <TrashIcon
                  fillWith="ud-primary"
                  boxSize="13px"
                  data-test="offerDetail.inactive.trash"
                />
                <Text size="medium" color="ud-primary">
                  Smazat
                </Text>
              </Stack>
            </Button>
            <CustomLink
              showUnderline
              to="/moje-schranka/[[...messages]]"
              onClick={() => {
                trackEvent({
                  "dl.eCat": "inzerce",
                  "dl.eAct": "click_zprávy",
                  "dl.eLab": "detail_inzeratu",
                });
              }}
              data-test="offerDetail.inactive.messages"
            >
              <Stack direction="row" align="center" spacing="4px">
                <Email2Icon
                  fillWith="ud-primary"
                  boxSize="13px"
                  data-test="offerDetail.inactive.messages.icon"
                />
                <Text size="medium" color="ud-primary">
                  Zprávy inzerátu
                </Text>
              </Stack>
            </CustomLink>
          </Stack>
        ) : (
          <Text
            textAlign={["center", "left"]}
            p={["20px", "unset"]}
            data-test="offerDetail.inactive.text"
          >
            Utekla vám nabídka bydlení? U té příští{" "}
            <b>buďte první s Hlídacím psem</b>.
          </Text>
        )}
        {type === "owner" ? (
          <Button
            onClick={() => {
              trackEvent({
                "dl.eCat": "inzerce",
                "dl.eAct": "zverejnit_znovu",
                "dl.eLab": "detail_inzeratu",
              });
              openAlertModal({
                title: "Opravdu chcete inzerát zveřejnit?",
                cancelText: "NE, děkuji",
                submitText: "ANO, zveřejnit",
                onSubmit: () => {
                  if (id) {
                    activateOnSubmit(id);
                  }
                },
              });
            }}
            data-test="offerDetail.inactive.activate"
          >
            Zveřejnit inzerát
          </Button>
        ) : (
          <CustomLink to="/hlidaci-pes">
            <Button
              leftIcon={<DogIcon fillWith="white" />}
              data-test="offerDetail.inactive.dog"
            >
              Vytvořit Hlídacího psa
            </Button>
          </CustomLink>
        )}
      </Stack>
    </Box>
  );
};

export default InactiveMessage;
